import type { ApiResponse } from '@creator/sdk/creator-sdk';
import { post as postApi } from '@creator/sdk/network/api-service';
import creatorSdk from '@src/services/creator-sdk';
import type { Comment, CommentSortOrder } from '@src/store/contentEntities/model';
import type { LinkMeta } from '@src/types/link-meta';
import { jotaiStore } from '../store';
import { commentCollectionAtomFamily, commentsAtom, setComment } from './comments';

export interface CreateAnonymousCommentApiRequestPayload {
    tokenName: string;
    postId: string;
    replyToCommentId: string;
    content: string;
}

export const createAnonymousComment = async (payload: CreateAnonymousCommentApiRequestPayload) => {
    const { tokenName, postId, replyToCommentId } = payload;
    const { data } = await postApi<ApiResponse<Comment>>(
        '/comment/anonymous',
        payload,
        {
            'X-Firebase-AppCheck': '',
        },
        false
    );

    // biome-ignore lint/suspicious/noExplicitAny: Backend response has commentId field
    const comment = { ...data.payload, id: (data.payload as any).commentId };
    setComment(comment);
    updateCommentCollection(tokenName, postId, comment.id, replyToCommentId);

    return comment;
};

export interface CreateCommentApiRequestPayload {
    publisherId?: string;
    tokenName: string;
    postId: string;
    replyToCommentId: string;
    content: string;
    taggedUsers?: { username: string; id: string }[];
    linkMeta?: LinkMeta | null;
}

export const createComment = async (payload: CreateCommentApiRequestPayload) => {
    const { tokenName, postId, replyToCommentId } = payload;
    const authIdToken = await creatorSdk.accountModule.getIdToken();

    const { data } = await postApi<ApiResponse<Comment>>(
        '/comment/create',
        payload,
        {
            Authorization: `Bearer ${authIdToken}`,
            'X-Firebase-AppCheck': '',
        },
        false
    );

    // biome-ignore lint/suspicious/noExplicitAny: Backend response has commentId field
    const comment = { ...data.payload, id: (data.payload as any).commentId };
    setComment(comment);

    updateCommentCollection(tokenName, postId, comment.id, replyToCommentId);
    return comment;
};

function updateCommentCollection(tokenName: string, postId: string, commentId: string, replyToCommentId?: string) {
    const type = replyToCommentId ? 'comment' : 'post';

    // For post-level comments, update all sortOrder variations
    // For replies, no sortOrder is used in the key
    const sortOrders: (CommentSortOrder | undefined)[] = type === 'post' ? ['best', 'newest', 'oldest', undefined] : [undefined];

    for (const sortOrder of sortOrders) {
        const targetCollectionAtom = commentCollectionAtomFamily({
            type,
            tokenName,
            postId,
            parentCommentId: replyToCommentId,
            ...(sortOrder && { sortOrder }),
        });

        // Prepend the new comment ID
        // or Append in case of replyToCommentId
        jotaiStore.set(targetCollectionAtom, draft => {
            if (!draft.ids) draft.ids = [];
            if (draft.ids.includes(commentId)) return;
            if (replyToCommentId) draft.ids.push(commentId);
            if (!replyToCommentId) draft.ids.unshift(commentId);
        });
    }

    // Update parent comment's numComments to show replies immediately
    if (replyToCommentId) {
        jotaiStore.set(commentsAtom, draft => {
            const parentComment = draft[replyToCommentId];
            if (parentComment) {
                parentComment.numComments = (parentComment.numComments || 0) + 1;
            }
        });
    }
}
